import clsx from 'clsx';
import React, { useCallback, useRef, useState } from 'react';
import { Observable } from 'rxjs';
import { SCHEMA_EXPLORER_ACTIVATION_UI_OPTIONS, DUPLICATE_COLUMN_ERROR_MESSAGE } from '../constants/constants';
import { Activation } from '../models/activation';
import { ACTIVATION_DRAFT_QUERY_TOOLTIP_MESSAGE } from '../strings';
import { RbacPermissions } from '../../../core/interfaces/user';
import { HdCoordinatedScrollAnchor } from '../../../a11y/scroll/components/coordinated-scroll/HdCoordinatedScroll';
import { HdButton, HdRbacButton } from '../../../components';
import {
  QueryEditorBadgeType,
  QueryExecutionResponse, QueryExecutionStatusEnum,
  QueryRunTypeEnum
} from '../../../warehouse-management/workbench/interfaces/workbench';
import { useDataSource } from '../../../warehouse-management/workbench/hooks/useDataSource';
import { Workbench } from '../../../warehouse-management/workbench/components/workbench/Workbench';
import TransformModelAPI from '../../transform-model/TransformModelAPI';
import { ActivationQueryHistoryDialog } from '../ActivationQueryHistoryDialog';
import { useTrackActivationQueryExecutionStatus } from '../useTrackActivationQueryExecutionStatus';

export interface ActivationPreviewWorkbenchProps {
  activation: Activation;
  savedQuery: string;
  currentQuery: string;
  queryEditorBadgeType: QueryEditorBadgeType;
  focusCodeEditor$?: Observable<any>;
  validateAndSaveClick: Function;
  revertToSavedQuery: Function;
  updateQueryState: (isDirty) => any;
  onQueryChange: (query: string) => any;
}

export function ActivationPreviewWorkbench({
  activation,
  savedQuery,
  currentQuery,
  queryEditorBadgeType,
  focusCodeEditor$,
  validateAndSaveClick,
  revertToSavedQuery,
  updateQueryState,
  onQueryChange
}: ActivationPreviewWorkbenchProps) {
  const [ query, setQuery ] = useState(currentQuery);
  const [ runQueryType, setRunQueryType ] = useState<QueryRunTypeEnum>(null);
  const [ queryExecutionResponse, setQueryExecutionResponse ] = useState<QueryExecutionResponse>(null);
  const [ queryHistoryDialogVisible, setQueryHistoryDialogVisible ] = useState(false);

  const {
    dataSource,
    dataSourceUIState,
    onSchemaDelete,
    getDataSource
  } = useDataSource({ dataSourceId: activation.warehouse.id });

  const coordinatedScrollAnchorRef = useRef(null);

  const { trackQueryExecutionStatus } = useTrackActivationQueryExecutionStatus();

  const onQueryExecutionResponseChange = (res: QueryExecutionResponse) => {
    trackQueryExecutionStatus(res, activation);
    setQueryExecutionResponse(res);
  };

  const showQueryHistoryDialog = () => {
    setQueryHistoryDialogVisible(true);
  };

  const onQueryHistoryDialogClose = (revertQuery) => {
    setQueryHistoryDialogVisible(false);

    if (revertQuery) {
      updateQueryState(false);
      setQuery(savedQuery);
      onQueryChange(savedQuery);
      revertToSavedQuery();
    }
  };

  const QueryEditorFooterActions = useCallback(({ runningQuery, runQuery, stopRunningQuery }) => {
    const onValidateAndSaveClick = () => {
      if (runningQuery) {
        stopRunningQuery();
      } else {
        setRunQueryType(QueryRunTypeEnum.RUN_CONTINUE);
        runQuery();
        validateAndSaveClick();
      }
    };

    return (
      <HdRbacButton
        rbacPermission={RbacPermissions.ACTIVATION_EDIT}
        className='ml-4'
        dataId={'validate-save-query'}
        onClick={onValidateAndSaveClick}
        icon={runQueryType === QueryRunTypeEnum.RUN_CONTINUE && runningQuery ? 'testing' : 'save'}
        disabled={(runningQuery && runQueryType !== QueryRunTypeEnum.RUN_CONTINUE) || !query || savedQuery === query}
      >
        {runQueryType === QueryRunTypeEnum.RUN_CONTINUE && runningQuery ? 'Stop Validating Query' : 'Validate & Save Query'}
      </HdRbacButton>
    );
  }, [ runQueryType, validateAndSaveClick ]);

  const QueryEditorHeaderActions = useCallback(({ isExpandedView }) => {
    if (isExpandedView || savedQuery === query) {
      return null;
    }

    return (
      <HdButton
        variation='outline'
        className='ml-4'
        dataId={'last-saved-query'}
        onClick={showQueryHistoryDialog}
        icon='history'
      >
        Last Saved Query
      </HdButton>
    );
  }, [ showQueryHistoryDialog ]);

  return (<>
    <HdCoordinatedScrollAnchor elRef={coordinatedScrollAnchorRef.current}>
      <div ref={coordinatedScrollAnchorRef}>
        <Workbench
          query={query}
          onQueryChange={(q) => {
            setQuery(q);
            updateQueryState(q !== savedQuery);
            onQueryChange(q);
          }}
          queryExecutionResponse={queryExecutionResponse}
          onQueryExecutionResponseChange={onQueryExecutionResponseChange}
          destinationId={activation.warehouse.id}
          dataSource={dataSource}
          dataSourceUIState={dataSourceUIState}
          getDataSource={getDataSource}
          onFolderDelete={onSchemaDelete}
          schemaExplorerUIOptions={SCHEMA_EXPLORER_ACTIVATION_UI_OPTIONS}
          stopQueryExecution$={TransformModelAPI.stopQueryExecution.bind(TransformModelAPI)}
          getQueryExecutionTaskId$={TransformModelAPI.getQueryExecutionTaskId.bind(TransformModelAPI)}
          getQueryExecutionTaskResponse$={TransformModelAPI.getQueryExecutionTaskResponse.bind(TransformModelAPI)}
          runQueryShortcutCommand='activation.runQueryAndContinue'
          rbacPermission={RbacPermissions.DESTINATION_EDIT}
          showingInsideCard
          onRunQueryClick={() => {
            setRunQueryType(QueryRunTypeEnum.RUN);
          }}
          queryEditorProps={{
            runQueryText: 'Test Query',
            queryEditorBadgeType,
            draftQueryTooltipMessage: ACTIVATION_DRAFT_QUERY_TOOLTIP_MESSAGE,
            QueryEditorFooterActions,
            QueryEditorHeaderActions
          }}
          duplicateColErrorMessage={DUPLICATE_COLUMN_ERROR_MESSAGE}
        />
      </div>
    </HdCoordinatedScrollAnchor>

    <ActivationQueryHistoryDialog
      open={queryHistoryDialogVisible}
      lastSavedQuery={savedQuery}
      onClose={onQueryHistoryDialogClose}
    />
  </>);
}
